import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import bookingSelectionConfig from '@/modules/base/components/booking-selection-list/config'
import List from '@/modules/base/components/list/list-v2'
import { fetchBankAccountList } from '@/redux/ducks/banking/bankAccountList'
import { setSelectedBookings } from '@/redux/ducks/bookingSelection/list'

import QuickFilter from '@/modules/base/components/quick-filter/quick-filter'
import Search from '@/modules/base/components/list/search/search'
import { FINANCE_API } from '@/modules/banking/constants'

// special cell renderer for ag-grid
import { AmountCell } from './custom-cells'

const { CLEARING_ACCOUNT_POS, CASH_JOURNAL_POS } = FINANCE_API.ACCOUNT_TYPE
const EXCLUDED_ACCOUNT_TYPES = [CLEARING_ACCOUNT_POS, CASH_JOURNAL_POS]
// special filter handling, activeFilter as state variable isn't working with ag-grids doesExternalFilterPass method
let activeFilter = 'all'

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    flex: 1,
  },
  defaultContainer: ({ hideTitle }) => ({
    position: 'relative',
    height: !hideTitle ? 'calc(100% - 125px)' : '100%',
  }),
  header: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 85,
  },
  title: {
    fontSize: 21,
    fontWeight: '500',
  },
  filterWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 12px 12px 0',
  },
  search: {
    width: 230,
    minWidth: 120,
    height: 32,
  },
  quickFilters: {
    display: 'flex',
    justifyContent: 'flex-end',
    minWidth: 0,
  },
  filter: {
    marginLeft: 20,
  },
}))

const BookingSelectionList = (props) => {
  const dispatch = useDispatch()
  const { bookingType, bookingStates, bookingData, listSettingName, fetchList, type, hideTitle, rowSelection } = props
  const classes = useStyles({ hideTitle })
  const listState = useSelector(state => state.lists)
  const { bankAccountList: { bankAccounts } } = useSelector(state => state.banking)
  const { items, selectedNodes } = listState[listSettingName]
  const [gridApi, setGridApi] = useState(null)
  const [filterItems, setFilterItems] = useState(bookingSelectionConfig.getFilterItems(listSettingName, bookingData, items, type))

  useEffect(() => {
    if (bankAccounts.length === 0) {
      dispatch(fetchBankAccountList())
    }
  }, [])

  useEffect(() => {
    setFilterItems(bookingSelectionConfig.getFilterItems(listSettingName, bookingData, items, type))
  }, [bookingData, items])

  useEffect(() => {
    if (filterItems.length && items.length) {
      let currentPriority = filterItems[0].priority
      activeFilter = 'all'
      filterItems.forEach((item) => {
        if (item.count && item.priority < currentPriority) {
          currentPriority = item.priority
          activeFilter = item.name
        }
      })
      gridApi && gridApi.onFilterChanged()
    }
  }, [items, filterItems, gridApi])

  useEffect(() => {
    if (bankAccounts.length > 0) {
      const excludedBankAccountIds = bankAccounts
        .filter(({ accountType }) => EXCLUDED_ACCOUNT_TYPES.includes(accountType))
        .map(({ id }) => id)

      dispatch(fetchList({ bookingType, bookingStates }, excludedBankAccountIds))
    }
  }, [bankAccounts])

  useEffect(() => {
    const selectedBookings = selectedNodes.map(booking => booking.data)
    dispatch(setSelectedBookings(selectedBookings))
  }, [selectedNodes])

  const onListReady = (api) => {
    setGridApi(api)
  }

  const onFilterClick = (newFilter) => {
    activeFilter = newFilter
    gridApi && gridApi.onFilterChanged()
  }

  const isExternalFilterPresent = () => {
    return true
  }

  const doesExternalFilterPass = (node) => {
    const currentFilterItem = filterItems.filter(item => item.name === activeFilter)

    if (currentFilterItem.length && currentFilterItem[0].matchItem) {
      return currentFilterItem[0].matchItem(activeFilter, node.data)
    } else {
      return true
    }
  }

  const getQuickFilter = () => {
    return filterItems.map((filter, index) => {
      return (
        <QuickFilter
          key={index}
          name={filter.name}
          label={filter.title}
          count={filter.count}
          active={activeFilter === filter.name}
          onClick={() => onFilterClick(filter.name)}
          customClass={classes.filter}
        />
      )
    })
  }

  const components = {
    amountCellRenderer: AmountCell,
  }

  return (
    <div className={classes.wrapper}>
      {!hideTitle && <div className={classes.header}>
        <span className={classes.title}>{lang.bookingSelectionList.title}</span>
      </div>}
      <div className={classes.defaultContainer}>
        <div className={classes.filterWrapper}>
          <Search
            customClass={classes.search}
            listSettingName={listSettingName}
            placeholder={lang.bookingSelectionList.searchPlaceholder}
            syncToUrl={false}
          />
          <div className={classes.quickFilters}>
            {getQuickFilter()}
          </div>
        </div>
        <List
          components={components}
          emptyText={lang.bookingSelectionList.emptyStateText}
          onListReady={onListReady}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          listSettingName={listSettingName}
          loaderText={lang.bookingSelectionList.loaderText}
          rowSelection={rowSelection}
          showToolPanel={false}
        />
      </div>
    </div>
  )
}

BookingSelectionList.propTypes = {
  hideTitle: PropTypes.bool,
  type: PropTypes.string,
  error: PropTypes.object,
  items: PropTypes.array,
  bookingType: PropTypes.oneOf(['all', 'positive', 'negative']),
  bookingStates: PropTypes.arrayOf(PropTypes.oneOf(['notAssigned', 'partiallyAssigned', 'fullyAssigned'])),
  bookingData: PropTypes.object.isRequired,
}

BookingSelectionList.defaultProps = {
  hideTitle: false,
  bookingType: 'all',
  bookingStates: [],
  hideMenuTabHeader: true,
}

export default BookingSelectionList
